import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import {
    Button,
    Glyphicon,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import {
    buildVDPBaseUrl,
    listingDataPreprocessing,
} from '@/utilities/dispatchUtils';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import ownersDuck from '@/ducks/ownersDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';

import fetchListingsAndFilters from '@/fetchers/srp/ListingsAndFiltersFetcher';

import useVdpNavigation from '@/hooks/useVdpNavigation';

const getCurrentQueryParams = () => {
    const currentURL = new URL(window.location.href);
    const searchParams = new URLSearchParams(currentURL.search);

    searchParams.delete('lnx');

    return Object.fromEntries(searchParams);
};

function VdpPaginationButtons({
    channel = 'ATC',
    listingId,
    listingIds = [],
    query = {},
}) {

    const {
        brand: [, {
            atc_branding: showAtcBranding,
            inventory_image_url: inventoryImageUrl,
        }],
        standard_listing_phone_display: [displayPhoneNumber],
        standard_listing_owner_name: [hideOwnerName],
        vdp_pagination_buttons: [, {
            size: paginationBtnSize,
        }],
    } = useFeatures([
        'brand',
        'standard_listing_phone_display',
        'standard_listing_owner_name',
        'vdp_pagination_buttons',
    ]);

    const { brand } = useBrand();

    const device = useDevice();
    const isXs = device?.is?.xs;

    const { firstRecord = 0, numRecords = 25 } = query;

    const navigateToVdp = useVdpNavigation();
    const dispatch = useDispatch();

    const [activeIndex, setActiveIndex] = useState(listingIds.findIndex((item) => item?.listingId === listingId));

    const [nextPage, setNextPage] = useState({});
    const [prevPage, setPrevPage] = useState({});

    const disableNext = _isEmpty(nextPage) && activeIndex === listingIds.length - 1;
    const disablePrev = _isEmpty(prevPage) && activeIndex === 0;

    const handleArrowClickPrevious = async (event) => {
        await sendClick('inventoryVdpPaginationClick', event, { type: 'previous' });
        const previousListing = listingIds[activeIndex - 1];
        let pageCount = firstRecord / numRecords;

        if (activeIndex === 0) {
            pageCount -= 1;
        }

        navigateToVdp({
            ...getCurrentQueryParams(),
            listingId: previousListing?.listingId,
            clickType: previousListing?.clickType || 'listing',
            firstRecord: (numRecords * pageCount),
            numRecords,
        });
    };

    const handleArrowClickNext = async (event) => {
        await sendClick('inventoryVdpPaginationClick', event, { type: 'next' });
        const nextListing = listingIds[activeIndex + 1];

        let pageCount = firstRecord / numRecords;
        if (activeIndex === listingIds.length - 1) {
            pageCount += 1;
        }

        navigateToVdp({
            ...getCurrentQueryParams(),
            listingId: nextListing?.listingId,
            clickType: nextListing?.clickType || 'listing',
            firstRecord: (numRecords * pageCount),
            numRecords,
        });
    };

    const updateListings = useCallback(async ({ type }) => {

        const isNext = type === 'next';

        const newFirstRecord = isNext ? (+firstRecord + +numRecords) : (+firstRecord - +numRecords);

        if (newFirstRecord >= 1000) {
            setNextPage({});
            return;
        }
        if (newFirstRecord <= 0) {
            setPrevPage({});
            return;
        }
        try {

            // Remove any listingId from the query to allow LSC fetcher to work properly
            // eslint-disable-next-line no-unused-vars
            const { listingId: queryListingId, ...fetchListingsQuery } = query;

            const response = await fetchListingsAndFilters({
                ...fetchListingsQuery,
                ...(channel && { channel: channel.toUpperCase() }),
                firstRecord: newFirstRecord,
            });

            const { owners = [], listings = [] } = response;

            const data = { brand };
            const vdpUrl = await buildVDPBaseUrl({
                query: fetchListingsQuery,
                data,
            });
            if (listings?.length) {
                listings.forEach((fetchedListing) => {
                    listingDataPreprocessing({
                        listing: fetchedListing,
                        owners,
                        displayPhoneNumber,
                        hideOwnerName,
                        inventoryImageUrl,
                        vdpUrl,
                    });
                });
            }

            if (newFirstRecord > firstRecord) {
                setNextPage(!_isEmpty(listings) ? { owners, listings } : {});
            }
            if (newFirstRecord < firstRecord) {
                setPrevPage(!_isEmpty(listings) ? { owners, listings } : {});
            }

            const newListings = isNext ? [
                listingId,
                ...listings.map((item) => item.id),
            ] : [
                ...listings.map((item) => item.id),
                listingId,
            ];

            dispatch(ownersDuck.creators.addOwners(owners));
            dispatch(inventoryDuck.creators.addInventory(listings));
            dispatch(srpResultsDuck.creators.setActiveResults(newListings));

        } catch (err) {
            setNextPage({});
            setPrevPage({});
        }
    }, [firstRecord, numRecords, query, channel, brand, listingId, dispatch, displayPhoneNumber, hideOwnerName, inventoryImageUrl]);

    useEffect(() => {
        setActiveIndex(listingIds.findIndex((item) => item?.listingId === listingId));
    }, [listingId, listingIds]);

    useEffect(() => {
        if (activeIndex === listingIds.length - 1) {
            updateListings({ type: 'next' });
        }

        if (activeIndex === 0) {
            updateListings({ type: 'previous' });
        }
    }, [activeIndex, updateListings, listingIds.length]);

    const buttonTextArray = [
        'Previous',
        'Next',
    ];

    const vdpNavBtnsComponent = buttonTextArray.map((element) => {
        const isPrev = element.includes('Prev');

        const buttonText = (btnTxt) => (
            <Text
                weight="bold"
            >
                {btnTxt}
            </Text>
        );

        return (
            <Button
                bsStyle="default"
                className={clsx('text-bold', {
                    disabled: isPrev ? disablePrev : disableNext,
                    'margin-left-2': !isPrev,
                })}
                onClick={isPrev ? handleArrowClickPrevious : handleArrowClickNext}
                key={`${element}-navigation`}
                bsSize={paginationBtnSize}
            >
                {!isPrev && buttonText(element)}
                <Glyphicon
                    key={element}
                    glyph={`chevron-${isPrev ? 'left' : 'right'}`}
                    className={clsx(`margin-${isPrev ? 'right' : 'left'}-1`, 'margin-bottom-1')}
                />
                {isPrev && buttonText(element)}
            </Button>
        );
    });

    return (
        <div
            data-cmp="vdpNavigation"
            className={clsx('text-nowrap margin-vertical-2', {
                'margin-left-auto': !showAtcBranding || isXs,
            })}
        >
            {vdpNavBtnsComponent}
        </div>
    );
}

VdpPaginationButtons.propTypes = {
    /**
     *  The channel identify brand to get correct data
     */
    channel: PropTypes.string,
    /**
     *  The id of current listing
     */
    listingId: PropTypes.number,
    /**
     *  All listing ids on a page
     */
    listingIds: PropTypes.array,
    /**
     *  Query data
     */
    query: PropTypes.object,
};

export default VdpPaginationButtons;
